import React, { useEffect, useState, useRef, useCallback } from 'react';
import styles from './ThinkingBubble.module.css';

interface ThinkingStep {
  id: number;
  type: 'plan' | 'search' | 'found' | 'analyze' | 'synthesize' | 'info';
  text: string;
  timestamp: number;
}

interface ThinkingBubbleProps {
  reasoning?: {
    data_source: string;
    reason: string;
    confidence: number;
    thinking?: string;
  } | null;
}

// Icon mapping for step types
const stepIcons: Record<ThinkingStep['type'], string> = {
  plan: '📋',
  search: '🔍',
  found: '✅',
  analyze: '📊',
  synthesize: '✍️',
  info: '💡',
};

// Parse thinking text to determine step type
const parseThinking = (text: string): ThinkingStep['type'] => {
  const lower = text.toLowerCase();
  if (lower.includes('plan') || lower.includes('queries') || lower.includes('research plan')) return 'plan';
  if (lower.includes('search') || lower.includes('looking') || lower.includes('בחיפוש')) return 'search';
  if (lower.includes('found') || lower.includes('results') || lower.includes('נמצאו')) return 'found';
  if (lower.includes('analyz') || lower.includes('evaluat') || lower.includes('מנתח')) return 'analyze';
  if (lower.includes('synthe') || lower.includes('final') || lower.includes('compil') || lower.includes('סיכום')) return 'synthesize';
  return 'info';
};

export const ThinkingBubble: React.FC<ThinkingBubbleProps> = ({ reasoning }) => {
  const [dots, setDots] = useState('');
  const [currentPhase, setCurrentPhase] = useState(0);
  const [displayText, setDisplayText] = useState('');
  const [steps, setSteps] = useState<ThinkingStep[]>([]);
  const [showAllSteps, setShowAllSteps] = useState(false);
  const stepCounter = useRef(0);
  const lastThinkingRef = useRef<string>('');
  const containerRef = useRef<HTMLDivElement>(null);

  // Define thinking phases with improved hardcoded flow
  const thinkingPhases = [
    "Analyzing your query...",
    "Deciding on best answer strategy",
    "Evaluating documents vs live search vs knowledge base",
    "Searching sources",
    "Summarizing reply"
  ];

  // Check if this is deep research
  const isDeepResearch = reasoning?.data_source === 'deep_research';

  // Animated dots
  useEffect(() => {
    const interval = setInterval(() => {
      setDots(prev => (prev === '...' ? '' : prev + '.'));
    }, 500);
    return () => clearInterval(interval);
  }, []);

  // Auto-progress through thinking phases for non-deep-research
  useEffect(() => {
    if (!reasoning || reasoning.data_source === 'analyzing') {
      const phaseInterval = setInterval(() => {
        setCurrentPhase(prev => (prev < thinkingPhases.length - 1 ? prev + 1 : prev));
      }, 2000);
      return () => clearInterval(phaseInterval);
    } else {
      setCurrentPhase(0);
    }
  }, [reasoning]);

  // Handle new thinking text for deep research - track as steps
  useEffect(() => {
    if (isDeepResearch && reasoning?.thinking) {
      const newThinking = reasoning.thinking;
      if (newThinking !== lastThinkingRef.current) {
        lastThinkingRef.current = newThinking;
        
        // Parse each line as a potential step
        const lines = newThinking.split('\n').filter(line => line.trim());
        lines.forEach(line => {
          // Check if this line is already in steps (avoid duplicates)
          const exists = steps.some(s => s.text === line);
          if (!exists) {
            stepCounter.current += 1;
            const newStep: ThinkingStep = {
              id: stepCounter.current,
              type: parseThinking(line),
              text: line,
              timestamp: Date.now(),
            };
            setSteps(prev => [...prev, newStep]);
          }
        });
      }
    }
  }, [reasoning?.thinking, isDeepResearch]);

  // Reset steps when reasoning changes to non-deep-research
  useEffect(() => {
    if (!isDeepResearch) {
      setSteps([]);
      stepCounter.current = 0;
      lastThinkingRef.current = '';
    }
  }, [isDeepResearch]);

  // Auto-scroll to bottom when new steps added
  useEffect(() => {
    if (containerRef.current && isDeepResearch) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [steps, isDeepResearch]);

  // Update display text for standard mode
  useEffect(() => {
    if (!reasoning) {
      setDisplayText('');
    } else if (reasoning.data_source === 'analyzing') {
      setDisplayText(thinkingPhases[currentPhase]);
    } else if (!isDeepResearch) {
      const sourceDisplay = reasoning.data_source.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      setDisplayText(`Using ${sourceDisplay}`);
    }
  }, [reasoning, currentPhase, isDeepResearch]);

  if (!reasoning) return null;

  // Deep Research enhanced UI
  if (isDeepResearch) {
    const visibleSteps = showAllSteps ? steps : steps.slice(-3);
    const hiddenCount = steps.length - visibleSteps.length;
    const currentStep = steps[steps.length - 1];

    return (
      <div className={styles.deepResearchContainer}>
        <div className={styles.deepResearchBubble}>
          <div className={styles.deepResearchHeader}>
            <span className={styles.deepResearchIcon}>🔬</span>
            <span className={styles.deepResearchTitle}>Deep Research</span>
            <span className={styles.stepCount}>{steps.length} steps</span>
          </div>
          
          <div className={styles.stepsContainer} ref={containerRef}>
            {hiddenCount > 0 && !showAllSteps && (
              <button 
                className={styles.showMoreBtn}
                onClick={() => setShowAllSteps(true)}
              >
                Show {hiddenCount} earlier steps...
              </button>
            )}
            
            {visibleSteps.map((step, idx) => {
              const isLatest = idx === visibleSteps.length - 1;
              return (
                <div 
                  key={step.id} 
                  className={`${styles.step} ${isLatest ? styles.currentStep : styles.completedStep}`}
                >
                  <span className={styles.stepIcon}>{stepIcons[step.type]}</span>
                  <span className={styles.stepText}>{step.text}</span>
                  {isLatest && <span className={styles.spinner}></span>}
                </div>
              );
            })}
            
            {steps.length === 0 && (
              <div className={styles.step}>
                <span className={styles.stepIcon}>🔬</span>
                <span className={styles.stepText}>Starting deep research{dots}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Standard thinking bubble for non-deep-research
  // Show source name bigger than the intent reason
  const sourceDisplay = reasoning?.data_source ? reasoning.data_source.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : '';
  const isAnalyzing = reasoning?.data_source === 'analyzing';
  
  return (
    <div className={styles.thinkingContainer}>
      <div className={styles.thinkingBubble}>
        <div className={styles.thinkingIcon}>
          <div className={styles.dot}></div>
          <div className={styles.dot}></div>
          <div className={styles.dot}></div>
        </div>
        <div className={styles.thinkingContent}>
          {isAnalyzing ? (
            <div className={styles.thinkingText}>
              {thinkingPhases[currentPhase]}
              <span className={styles.dots}>{dots}</span>
            </div>
          ) : (
            <>
              <div className={styles.sourceLabel}>{sourceDisplay}</div>
              {reasoning.reason && (
                <div className={styles.intentText}>{reasoning.reason}</div>
              )}
            </>
          )}
          {reasoning.thinking && !isDeepResearch && (
            <div className={styles.thinkingDetails}>
              {reasoning.thinking.split('\n').map((line, i) => (
                <div key={i} className={styles.thinkingLine}>{line}</div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};